// Our own tooltip instead of the browser's slow `title=` (D-062). Shows after 400 ms on hover, on
// keyboard focus, and at once when moving between neighbours; never on touch, never on large content
// areas (a message body is not a control). Escape hides it.
import { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { layerRoot, placeLayer } from './layer';

const HOVER_DELAY = 400;
const FOCUS_DELAY = 150;
/** Larger than this (px²) is a content area, not a control: no tooltip. */
const MAX_AREA = 60_000;
let warmUntil = 0;

export function mergeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (v: T | null) => { for (const r of refs) { if (typeof r === 'function') r(v); else if (r) (r as { current: T | null }).current = v; } };
}

export interface TooltipProps {
  content: ReactNode;
  /** A keyboard shortcut shown after the text ("E", "⌘K"). */
  shortcut?: string;
  placement?: 'top' | 'bottom' | 'right';
  disabled?: boolean;
  /** One element that can take a ref (a button, a link, an IconButton). */
  children: ReactElement;
}

export function Tooltip({ content, shortcut, placement = 'top', disabled, children }: TooltipProps) {
  const id = useId();
  const el = useRef<HTMLElement | null>(null);
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  const [shown, setShown] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const hide = useCallback(() => { clearTimeout(timer.current); setShown((s) => { if (s) warmUntil = Date.now() + 300; return false; }); setPos(null); }, []);
  const show = (delay: number) => {
    if (disabled || !content) return;
    const t = el.current; if (!t) return;
    const r = t.getBoundingClientRect();
    if (r.width * r.height > MAX_AREA) return;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(true), Date.now() < warmUntil ? 0 : delay);
  };
  useLayoutEffect(() => {
    if (!shown || !tip.current || !el.current) return;
    const r = el.current.getBoundingClientRect();
    if (placement === 'right') {
      const w = tip.current.offsetWidth; const h = tip.current.offsetHeight;
      const rtl = getComputedStyle(el.current).direction === 'rtl';
      const left = rtl ? r.left - w - 6 : r.right + 6;
      setPos({ left: Math.max(4, Math.min(left, window.innerWidth - w - 4)), top: Math.max(4, Math.min(r.top + (r.height - h) / 2, window.innerHeight - h - 4)) });
      return;
    }
    const p = placeLayer({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }, tip.current.offsetWidth, tip.current.offsetHeight, placement === 'top' ? 'top' : 'bottom', 6);
    setPos({ left: p.left, top: p.top });
  }, [shown, placement]);
  useEffect(() => {
    if (!shown) return;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); hide(); } };
    document.addEventListener('keydown', key, true);
    document.addEventListener('scroll', hide, true);
    return () => { document.removeEventListener('keydown', key, true); document.removeEventListener('scroll', hide, true); };
  }, [shown, hide]);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!isValidElement(children)) return children;
  const props = children.props as Record<string, unknown> & { ref?: Ref<HTMLElement> };
  const call = (name: string, e: unknown) => (props[name] as ((e: unknown) => void) | undefined)?.(e);
  // A tooltip that only repeats the control's name isn't announced twice.
  const same = typeof content === 'string' && props['aria-label'] === content;
  const describedBy = [props['aria-describedby'] as string | undefined, shown && !same ? id : undefined].filter(Boolean).join(' ') || undefined;
  const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
    ref: mergeRefs(props.ref, (v: HTMLElement | null) => { el.current = v; }),
    'aria-describedby': describedBy,
    onPointerEnter: (e: PointerEvent) => { call('onPointerEnter', e); if (e.pointerType === 'mouse') show(HOVER_DELAY); },
    onPointerLeave: (e: PointerEvent) => { call('onPointerLeave', e); hide(); },
    onPointerDown: (e: PointerEvent) => { call('onPointerDown', e); hide(); },
    onFocus: (e: FocusEvent) => { call('onFocus', e); let kb = true; try { kb = (e.target as HTMLElement).matches(':focus-visible'); } catch { /* old browser */ } if (kb) show(FOCUS_DELAY); },
    onBlur: (e: FocusEvent) => { call('onBlur', e); hide(); },
  });
  return (
    <>
      {trigger}
      {shown && createPortal(
        <div ref={tip} id={id} role="tooltip" className="dz-tooltip" style={pos ?? { left: 0, top: 0, visibility: 'hidden' }}>
          {content}{shortcut && <kbd className="dz-tooltip__key">{shortcut}</kbd>}
        </div>, layerRoot(el.current))}
    </>
  );
}
